import React, { useEffect, useState } from "react";
import { getDayOfWeek, optionsInGerman } from "../../utils/Utils";
import { weekOrder } from "../../utils/DataFormatter";
import { cardData } from "../../utils/DataUtils";

const Dropdown = ({
  handleDropdownData,
  data,
}: {
  handleDropdownData: (selectedDays: Set<string>[]) => void;
  data: cardData[];
}) => {
  const [isOpen, setIsOpen] = useState(false); // Dropdown visibility state
  const [selectedDays, setSelectedDays] = useState<Set<string>[]>([]); // Selected day sets
  const [dayCombinations, setDayCombinations] = useState<Set<string>[]>([]); // Day combinations from data

  // Function to extract unique day combinations
  const extractUniqueDays = () => {
    const combinations: Set<string>[] = [];
    const seenCombinations = new Set<string>(); // To track unique combinations by string representation

    // Assume vkuData is available and properly structured
    data.forEach((event) => {
      const uniqueDays = [];
      if (event.fDate) uniqueDays.push(getDayOfWeek(event.fDate));
      if (event.sDate) uniqueDays.push(getDayOfWeek(event.sDate));
      if (event?.tDate) uniqueDays.push(getDayOfWeek(event.tDate));

      // const sortedDays = uniqueDays.sort((a, b) => weekOrder[a] - weekOrder[b]);
      const sortedDays = [...uniqueDays].sort();
      const combinedDates = sortedDays.join(",");

      // Only add unique combinations to the result
      if (!seenCombinations.has(combinedDates)) {
        seenCombinations.add(combinedDates);
        combinations.push(new Set(uniqueDays));
      }
    });

    return combinations;
  };

  const toggleDropdown = () => setIsOpen((prev) => !prev);

  // Handle selecting/deselecting a combination
  const handleCheckboxChange = (combination: Set<string>) => {
    setSelectedDays((prevSelectedDays) => {
      // Check if the combination is already selected
      const combinationIndex = prevSelectedDays.findIndex(
        (selectedSet) =>
          combination.size === selectedSet.size &&
          [...combination].every((day) => selectedSet.has(day))
      );

      let updatedSelectedDays = [...prevSelectedDays];

      if (combinationIndex !== -1) {
        // If already selected, remove it
        updatedSelectedDays.splice(combinationIndex, 1);
      } else {
        // If not selected, add it
        updatedSelectedDays.push(combination);
      }

      // handleDropdownData(updatedSelectedDays);
      return updatedSelectedDays;
    });
  };

  useEffect(() => {
    handleDropdownData(selectedDays); // Pass the updated selectedDays to the parent component
  }, [selectedDays]); // Trigger when selectedDays changes

  // Update the combinations of days when the component mounts
  useEffect(() => {
    setDayCombinations(extractUniqueDays());
  }, [isOpen]);

  return (
    <div className="relative inline-block w-64">
      {/* Dropdown Button */}
      <button
        onClick={toggleDropdown}
        className="bg-blue-600 text-white px-4 py-2 w-full rounded-lg flex justify-between items-center focus:outline-none"
      >
        <span>
          {selectedDays.length > 0
            ? selectedDays
                .map((set, index) => {
                  const days = Array.from(set)
                    .map((day) => optionsInGerman[day] || day)
                    .join(" & ");
                  return index === 0 ? days : null;
                })
                .filter(Boolean)
                .join(", ") + (selectedDays.length > 1 ? " ..." : "")
            : "Wochentag"}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 transform transition-transform ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-20 mt-2 py-1 w-full bg-white shadow-lg rounded-lg border border-gray-200">
          {dayCombinations.map((combination, index) => {
            const combinationLabel = Array.from(combination)
              .map((day) => optionsInGerman[day] || day)
              .join(" & ");
            const isSelected = selectedDays.some(
              (selectedSet) =>
                selectedSet.size === combination.size &&
                [...combination].every((day) => selectedSet.has(day))
            );
            return (
              <label
                key={index}
                className="flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100"
              >
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => handleCheckboxChange(combination)}
                  className="form-checkbox accent-light-blue outline-dark-blue h-5 w-5 rounded hover:cursor-pointer"
                />
                <span className="ml-3 text-dark-blue font-content text-md">
                  {combinationLabel}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Dropdown;
